import { useCallback } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { Amount } from "app/components/ui/Amount/Amount";
import PaymentCouponItem from "app/pages/Booking/Payment/PaymentCoupons/PaymentCouponItem/PaymentCouponItem";
import MoreDetails from "app/pages/.shared/MoreDetails";
import "../CreditRadio/CreditRadio.scss";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import { intervalToDuration } from "date-fns";
import { SHOW_CREDIT_NOTES_EXPIRATION_MAX_YEAR } from "app/constants";
import RadioFormik from "app/pages/.shared/form/RadioFormik";
import { LessIcon, MoreIcon } from "app/icons/navigation";
import { ICON_SIZES } from "app/constants";

const CreditNoteRadio = ({ value, usableCreditNotes, unusableCreditNotes }) => {
	const { track } = useAnalytics();

	const handleOnClick = useCallback(() => {
		track("more_details_refund");
	}, []);

	const today = Date.now();

	return (
		<RadioFormik
			showIcon={true}
			name="credit-note-radio"
			id="credit-note-radio"
			checked={true}
			data-testid={`coupon-credit-note-input`}
			body={
				<div className="credit-radio__body">
					<MoreDetails
						headerPosition={"bottom"}
						expandedLabel={
							<>
								<LessIcon size={ICON_SIZES.XS} />
								<FormattedMessage id="product.page.less.details" />
							</>
						}
						collapsedLabel={
							<>
								<MoreIcon size={ICON_SIZES.XS} />
								<FormattedMessage id="product.page.more.details" />
							</>
						}
						onChange={handleOnClick}
					>
						{usableCreditNotes.map((usableCredit = {}) => {
							let showExpirationDate;

							if (usableCredit.maxUseDate) {
								const duration = intervalToDuration({
									start: today,
									end: usableCredit.maxUseDate,
								});

								// intervalToDuration can returns an object with years, months, days, etc. with negative values and undefined if value is 0
								const yearCount = Math.abs(duration.years || 0);

								showExpirationDate =
									yearCount <= SHOW_CREDIT_NOTES_EXPIRATION_MAX_YEAR;
							}

							return (
								<div className="credit-radio__coupon-item" key={usableCredit.title}>
									<PaymentCouponItem
										friendEmail={usableCredit.friendEmail}
										amount={usableCredit.amount}
										creationDate={usableCredit.creationDate}
										balance={usableCredit.balance}
										uses={usableCredit.uses}
										maxUseDate={showExpirationDate && usableCredit.maxUseDate}
										conditions={usableCredit.conditions}
										usageRule={
											<FormattedMessage id="payment.coupons.credit.note.usage.rule" />
										}
										title={usableCredit.title}
										usableAmount={usableCredit.usableAmount}
									/>
								</div>
							);
						})}

						{unusableCreditNotes.map((unusableCredit = {}) => {
							let showExpirationDate;

							if (unusableCredit.maxUseDate) {
								const duration = intervalToDuration({
									start: today,
									end: unusableCredit.maxUseDate,
								});

								// intervalToDuration can returns an object with years, months, days, etc. with negative values and undefined if value is 0
								const yearCount = Math.abs(duration.years || 0);

								showExpirationDate =
									yearCount <= SHOW_CREDIT_NOTES_EXPIRATION_MAX_YEAR;
							}

							return (
								<div
									className="credit-radio__coupon-item"
									key={unusableCredit.title}
								>
									<PaymentCouponItem
										disabled={true}
										friendEmail={unusableCredit.friendEmail}
										isApplicable={unusableCredit.isApplicable}
										amount={unusableCredit.amount}
										creationDate={unusableCredit.creationDate}
										balance={unusableCredit.balance}
										uses={unusableCredit.uses}
										maxUseDate={showExpirationDate && unusableCredit.maxUseDate}
										conditions={unusableCredit.conditions}
										title={unusableCredit.title}
										usableAmount={unusableCredit.usableAmount}
									/>
								</div>
							);
						})}
					</MoreDetails>
				</div>
			}
		>
			<div className="credit-radio__content">
				<div className="credit-radio__name">
					{usableCreditNotes.length === 1 ? (
						usableCreditNotes[0]?.title
					) : (
						<FormattedMessage id="payment.coupons.input.creditnotes.label" />
					)}
				</div>
				<div className="credit-radio__amount">
					<Amount value={value} sign="-" signIsBold={false} size="xl" />
				</div>
			</div>
		</RadioFormik>
	);
};

CreditNoteRadio.propTypes = {
	value: PropTypes.number,
	usableCreditNotes: PropTypes.array,
	unusableCreditNotes: PropTypes.array,
};

CreditNoteRadio.defaultProps = {
	usableCreditNotes: [],
	unusableCreditNotes: [],
};

export default CreditNoteRadio;
